import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useIntl } from 'react-intl';

import { useUCMenuItems } from '@atlassian/navigation-bff-data-provider/universal-create';

import { useMenuStateContext } from '../../../../context';
import { sectionsToAppMenuConfig } from '../../MenuContent/utils/configConverters';

import { useFetchUser } from './useFetchUser';
import { usePrefetchLoom } from './usePrefetchLoom';

interface usePrefetchReturn {
	triggerPrefetch: () => void;
}

/**
 * Prefetch hook that will try to call the backend endpoint to get list of menu items
 * Loom is NOT included. TODO: add loom preload separately https://hello.jira.atlassian.cloud/browse/NAVX-2470
 * User is conditionally fetched based on return items from the backend
 *
 * @returns Object with triggerPrefetch function and prefetch requirement flags
 */
export function usePrefetch(): usePrefetchReturn {
	const { sections, product, subProduct, crossAppCreationConfig } = useMenuStateContext();
	const { locale } = useIntl();

	const shouldSkip = !crossAppCreationConfig.enabled;
	const shouldFetchData =
		!shouldSkip && crossAppCreationConfig.enabled && crossAppCreationConfig.prefetch === true;

	const { triggerPrefetch: triggerPrefetchLoom } = usePrefetchLoom();

	const appMenuConfig = useMemo(() => sectionsToAppMenuConfig(sections), [sections]);
	const { fetchUserIfNeeded } = useFetchUser();

	/**
	 * Fetching menu items
	 */
	const { data, fetchUCMenuItems } = useUCMenuItems({
		cloudId: crossAppCreationConfig.enabled ? crossAppCreationConfig.cloudId : '',
		orgId: crossAppCreationConfig.enabled ? crossAppCreationConfig.orgId : '',
		userId: crossAppCreationConfig.enabled ? crossAppCreationConfig.accountId : '',
		product,
		locale,
		subProduct,
		appMenuConfig,
		skipInitialization: !shouldFetchData,
	});

	const prefetchTriggered = useRef(false);
	const userFetchTriggered = useRef(false);

	const triggerPrefetch = useCallback(() => {
		if (prefetchTriggered.current || !shouldFetchData) {
			return;
		}
		prefetchTriggered.current = true;
		triggerPrefetchLoom();
		fetchUCMenuItems();
	}, [shouldFetchData, fetchUCMenuItems, triggerPrefetchLoom]);

	// Reactively fetch user data when sections become available
	useEffect(
		function fetchUserWhenDataReady() {
			if (!shouldFetchData || !data?.sections || userFetchTriggered.current) {
				return;
			}

			userFetchTriggered.current = true;
			fetchUserIfNeeded({ sections: data.sections });
		},
		[shouldFetchData, data?.sections, fetchUserIfNeeded],
	);

	return {
		triggerPrefetch,
	};
}
